import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'userDisplayName',
    pure: true,
    standalone: false
})
export class UserDisplayNamePipe implements PipeTransform {
  transform(
    record: any,
    options?: {
      format?: string,
      case?: string,
      forceReal?: boolean
    }
  ): string {
    // Make sure the record passed is a valid object to build from. Return empty string if not a valid populated object.
    if (!record || Array.isArray(record) || typeof record !== 'object' || Object.keys(record).length === 0) return '';

    const allowedFormats = ['firstLast', 'lastFirst', 'firstInitialLast', 'initials', 'firstOnly', 'lastOnly', 'firstInitial', 'lastInitial'];
    const allowedCaseTypes = ['lower', 'upper', 'title', 'none']; // Pass case style none to use whatever was set on field originally.

    // Setup local variables to use for build.
    const format = allowedFormats.includes(options?.format ?? '') ? options?.format : 'firstLast';
    let caseStyle = allowedCaseTypes.includes(options?.case ?? '') ? options?.case : 'title';
    const useReal = options?.forceReal ?? false;

    // Force uppercase for initials if no case explicitly provided
    if (['initials', 'firstInitial', 'lastInitial'].includes(format) && !options?.case) caseStyle = 'upper';

    // Setup fallbacks for no first/last fields but name field populated. In case just a name field present.
    const nameParts = (record?.name ?? '').trim().split(' ');
    const backupFirstName = nameParts[0] || '';
    const backupLastName = (nameParts.length > 1 ? nameParts.slice(1).filter(Boolean).join(' ').trim() : '') || '';

    // Try to assign fields from record. Use backups if not present.
    const first = ((useReal ? record?.first_name : record?.preferred_first_name) || record?.first_name || backupFirstName || '').trim();
    const last = ((useReal ? record?.last_name : record?.preferred_last_name) || record?.last_name || backupLastName || '').trim();

    // Apply name formats now.
    let name = '';
    switch (format) {
      case 'lastFirst':
        name = last && first ? `${last}, ${first}` : last || first;
        break;

      case 'firstInitialLast':
        const initial = first ? first.charAt(0) + '.' : '';
        name = [initial, last].filter(Boolean).join(' ');
        break;

      case 'initials':
        name = (first ? first.charAt(0) : '') + (last ? last.charAt(0) : '');
        break;

      case 'firstInitial':
        name = first ? first.charAt(0) + '.' : '';
        break;

      case 'lastInitial':
        name = last ? last.charAt(0) + '.' : '';
        break;

      case 'firstOnly':
        name = first || 'Unknown';
        break;

      case 'lastOnly':
        name = last || 'Unknown';
        break;

      default: // 'firstLast'
        name = [first, last].filter(Boolean).join(' ');
    }

    // Normalize any leftover double spaces: safe guard.
    name = name.replace(/\s+/g, ' ').trim();

    // Assign needed name casing now.
    switch (caseStyle) {
      case 'lower': return name.toLowerCase();
      case 'upper': return name.toUpperCase();
      case 'title': return this.toTitleCase(name);
      default: return name;
    }
  }

  private toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, txt =>
      txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    );
  }
}